/**
 * `<CandidateStatusSelect>` — inline, in-place pipeline stage-advance control.
 *
 * V1 parity: reuses `useUpdateCandidateStatus` (PUT /candidates/{id}). Unlike the
 * V1 `<CandidateStatusDropdown>` (explicit-Save), the inline table/card surface
 * applies immediately on select — the stage advances in place, a toast confirms,
 * and on error the draft rolls back to the server-confirmed value. This is the
 * shared control for BOTH the v2 Candidates table's Status column and the v2
 * CandidateCard footer, so a recruiter never has to open a profile to move a
 * candidate forward.
 *
 * Click isolation: the trigger sits inside clickable table rows / cards. Pointer
 * + click events are stopped at the wrapper so opening the dropdown never fires
 * the row/card navigation. The Radix content is portaled, so item selection can't
 * bubble to the row either.
 *
 * Token discipline: chrome via the vendored (tokenized) Select primitive + v2
 * semantic utilities. No raw color, no hsl(var(--hex)). Guard-clean.
 */
import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdateCandidateStatus } from "@/hooks/queries/candidates/useUpdateCandidateStatus";
import { useToastStore } from "@/stores/useToastStore";
import type { CandidateStatus } from "@/components/candidates/StatusPill";
import { cn } from "@/lib/utils";

// ── Ordered pipeline stages (V1's canonical 8-value set) ─────────────────────
const STATUS_OPTIONS: CandidateStatus[] = [
  "active",
  "phone_screen",
  "interview_scheduled",
  "interviewed",
  "offer",
  "hired",
  "rejected",
  "opted_out",
];

const STATUS_LABELS: Record<CandidateStatus, string> = {
  active: "Active",
  phone_screen: "Phone screen",
  interview_scheduled: "Interview scheduled",
  interviewed: "Interviewed",
  offer: "Offer",
  hired: "Hired",
  rejected: "Rejected",
  opted_out: "Opted out",
};

interface CandidateStatusSelectProps {
  candidateId: string;
  currentStatus: CandidateStatus;
  /** Extra classes on the trigger (e.g. min-w constraints per surface). */
  className?: string;
}

export function CandidateStatusSelect({
  candidateId,
  currentStatus,
  className,
}: CandidateStatusSelectProps): JSX.Element {
  // Local draft gives an immediate optimistic label; server truth (currentStatus)
  // re-syncs it on refetch, and an error rolls it back.
  const [draft, setDraft] = useState<CandidateStatus>(currentStatus);
  const mutation = useUpdateCandidateStatus();
  const addToast = useToastStore((s) => s.addToast);

  useEffect(() => {
    setDraft(currentStatus);
  }, [currentStatus]);

  const onChange = (next: CandidateStatus): void => {
    if (next === draft) return;
    setDraft(next);
    mutation.mutate(
      { id: candidateId, status: next },
      {
        onSuccess: () => addToast("Status updated", "success"),
        onError: (err) => {
          setDraft(currentStatus);
          addToast(
            err.message || "Couldn't update status — try again.",
            "error",
          );
        },
      },
    );
  };

  return (
    // Isolate row/card navigation: stop the open gesture from bubbling.
    <div
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Select
        value={draft}
        onValueChange={(v) => onChange(v as CandidateStatus)}
        disabled={mutation.isPending}
      >
        <SelectTrigger
          aria-label="Change pipeline stage"
          className={cn(
            "h-7 w-full min-w-0 gap-1 rounded-full border-border-default bg-surface-card px-2.5 py-0 text-[11px] font-medium text-text-primary shadow-none",
            "hover:border-border-strong",
            className,
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map((s) => (
            <SelectItem key={s} value={s}>
              {STATUS_LABELS[s]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
